import instanceJWT from "./apiJwt";
import type { RetryAxiosRequestConfig } from "../models/api/RetryAxiosRequestConfig";
import type {
  BillingAccountUpdatePayload,
  BillingBalance,
  BillingClientsPage,
  BillingHistoryFilters,
  BillingHistoryPage,
  BillingPaymentLink,
  BillingPricingRulesFilters,
  BillingPricingRulesPage,
  BillingProfileDetail,
  BillingProfilesPage,
  BillingTariffInvoicePayload,
  BillingTariffPayPayload,
  BillingTariffsPage,
  BillingTariffVersionsPage,
  BillingTokenPrice,
  BillingUsageDailyFilters,
  BillingUsageDailyPage,
} from "../models/billing/Billing";

// Cabinet-методы биллинга проксируются ЛК под `api/eplex/billing/cabinet/*`
// (префикс `/v1/billing` nginx подставляет сам).
const BASE = "eplex/billing/cabinet";
// Методы оплаты лежат в бэке ЛК отдельно от биллинга — без `eplex`-проксирования.
const PAY_BASE = "billing";

function managedClientHeader(managedClientId: number | string | null | undefined): Record<string, string> {
  if (managedClientId === null || managedClientId === undefined || managedClientId === "") return {};
  return { "X-Managed-ClientId": String(managedClientId) };
}

function unwrap<T>(data: unknown): T {
  if (data && typeof data === "object" && "data" in (data as Record<string, unknown>)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

export async function fetchBillingClients(): Promise<BillingClientsPage> {
  const response = await instanceJWT.get(`${BASE}/clients`);
  return unwrap<BillingClientsPage>(response.data);
}

export async function fetchBillingBalance(clientId: number | string): Promise<BillingBalance> {
  const response = await instanceJWT.get(`${BASE}/balance`, {
    params: { client_id: clientId },
  });
  return unwrap<BillingBalance>(response.data);
}

export async function fetchBillingHistory(filters: BillingHistoryFilters): Promise<BillingHistoryPage> {
  const params: Record<string, string | number> = { client_id: filters.client_id };
  const optional: (keyof BillingHistoryFilters)[] = [
    "profile_id",
    "product_id",
    "event_id",
    "operation_type",
    "status",
    "transaction_id",
    "freeze_id",
    "external_operation_id",
    "created_from",
    "created_to",
    "limit",
    "cursor",
  ];
  for (const key of optional) {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  }
  const response = await instanceJWT.get(`${BASE}/history`, { params });
  return unwrap<BillingHistoryPage>(response.data);
}

/**
 * Суточный расход токенов за период — готовый плотный ряд с бэка (ledger).
 * Заменяет обход истории операций и классификацию их типов на клиенте: у
 * истории потолок `limit: 200`, и активному клиенту одной страницы на период
 * не хватало (CAB-725).
 */
export async function fetchBillingUsageDaily(filters: BillingUsageDailyFilters): Promise<BillingUsageDailyPage> {
  // Спарклайн — вспомогательный: при сбое он просто скрыт, глобальный тост здесь лишний.
  const response = await instanceJWT.get(`${BASE}/usage/daily`, {
    params: { client_id: filters.client_id, from: filters.from, to: filters.to },
    _silent: true,
  } as RetryAxiosRequestConfig);
  return unwrap<BillingUsageDailyPage>(response.data);
}

export async function fetchBillingProfiles(
  managedClientId: number | string,
  options?: { status?: string },
): Promise<BillingProfilesPage> {
  const response = await instanceJWT.get(`${BASE}/profiles`, {
    headers: managedClientHeader(managedClientId),
    params: options?.status ? { status: options.status } : undefined,
  });
  return unwrap<BillingProfilesPage>(response.data);
}

export async function fetchBillingProfileDetail(
  managedClientId: number | string,
  externalProfileId: number | string,
): Promise<BillingProfileDetail> {
  const response = await instanceJWT.get(`${BASE}/profiles/${externalProfileId}`, {
    headers: managedClientHeader(managedClientId),
  });
  return unwrap<BillingProfileDetail>(response.data);
}

export async function updateBillingProfileAccount(
  managedClientId: number | string,
  externalProfileId: number | string,
  payload: BillingAccountUpdatePayload,
): Promise<BillingProfileDetail> {
  const response = await instanceJWT.put(`${BASE}/profiles/${externalProfileId}/account`, payload, {
    headers: managedClientHeader(managedClientId),
  });
  return unwrap<BillingProfileDetail>(response.data);
}

export async function disableBillingProfileAccount(
  managedClientId: number | string,
  externalProfileId: number | string,
): Promise<void> {
  await instanceJWT.delete(`${BASE}/profiles/${externalProfileId}/account`, {
    headers: managedClientHeader(managedClientId),
  });
}

export async function fetchBillingTariffs(): Promise<BillingTariffsPage> {
  const response = await instanceJWT.get(`${BASE}/tariffs`);
  return unwrap<BillingTariffsPage>(response.data);
}

/** Версии тарифа: прайсинг по длительности (`metadata.price_by_months`) живёт здесь. */
export async function fetchBillingTariffVersions(code: string): Promise<BillingTariffVersionsPage> {
  const response = await instanceJWT.get(`${BASE}/tariffs/${encodeURIComponent(code)}/versions`);
  return unwrap<BillingTariffVersionsPage>(response.data);
}

/** Стоимость операций в токенах — у биллинга это ручка operation-prices (pricing-rules в кабинетном API нет). */
export async function fetchBillingPricingRules(filters?: BillingPricingRulesFilters): Promise<BillingPricingRulesPage> {
  const params: Record<string, string> = {};
  if (filters?.operation_code) params.operation_code = filters.operation_code;
  if (filters?.active_at) params.active_at = filters.active_at;
  const response = await instanceJWT.get(`${BASE}/operation-prices`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return unwrap<BillingPricingRulesPage>(response.data);
}

/** Покупка/продление/плановая смена тарифа: возвращает ссылку на оплату (LK-слой). */
export async function payTariff(payload: BillingTariffPayPayload): Promise<BillingPaymentLink> {
  const response = await instanceJWT.post(`${PAY_BASE}/tariff/pay`, payload);
  return unwrap<BillingPaymentLink>(response.data);
}

/**
 * Возврат клиента на бесплатный тариф (LK-слой, CAB-602 п.1).
 *
 * Отдельно от `tariff/pay`: там всегда создаётся платёж, а бесплатный тариф
 * платить нечем — запрос заканчивался 400 «недоступен указанный период оплаты».
 * Тела нет: тариф на бэке прошит, этой ручкой платный не подключить.
 */
export async function applyFreeTariff(): Promise<void> {
  await instanceJWT.post(`${PAY_BASE}/tariff/free`);
}

/** Докупка токенов: возвращает ссылку на оплату (LK-слой). */
export async function accrueTokens(tokens: number, agreementAccepted: boolean): Promise<BillingPaymentLink> {
  const response = await instanceJWT.post(`${PAY_BASE}/tokens/accrual`, {
    tokens,
    agreementAccepted,
  });
  return unwrap<BillingPaymentLink>(response.data);
}

/** Фиксированная цена докупки одного постоянного токена (LK-слой). */
export async function fetchTokenPrice(): Promise<BillingTokenPrice> {
  const response = await instanceJWT.get(`${PAY_BASE}/token/price`);
  return unwrap<BillingTokenPrice>(response.data);
}

/** Счёт-договор для юр. лиц по выбранному тарифу: ответ — бинарный .docx (LK-слой). */
export function fetchTariffInvoice(payload: BillingTariffInvoicePayload) {
  return instanceJWT.post(`${PAY_BASE}/tariff/invoice`, payload, {
    responseType: "blob",
    _silent: true,
  } as RetryAxiosRequestConfig);
}
